import { CSSProperties, RefObject, useCallback } from "react";
import cx from "classnames";

import { ROW_HEIGHT_ESTIMATE } from "ds/components/TableSimple/constants";
import TableSimpleRow from "ds/components/TableSimple/Row";
import { TableCellPerColumn } from "ds/components/TableSimple/types";
import { TableColumnMeta } from "ds/components/Table/types";
import { TreeNode } from "utils/tree";
import { SpaceWithParentAccessLevel } from "views/Account/Spaces/types";

import styles from "./styles.module.css";
import useSpacesListStickyRowEviction from "./useStickyRowEviction";

type SpacesListStickyGroupRowProps = {
  node: TreeNode<SpaceWithParentAccessLevel>;
  level: number;
  columns: TableColumnMeta[];
  isFirstColumnPinned: boolean;
  cellPerColumn: TableCellPerColumn<TreeNode<SpaceWithParentAccessLevel>>;
  keyboardProps: Record<string, unknown>;
  measureRef: (node: HTMLDivElement | null) => void;
  dataIndex: number;
  isActiveSticky: boolean;
  evictionAnchor?: number;
  tableRef: RefObject<HTMLDivElement | null>;
  className?: string;
  style?: CSSProperties;
  ariaLevel: number;
  ariaExpanded?: boolean;
};

const SpacesListStickyGroupRow = ({
  node,
  level,
  columns,
  isFirstColumnPinned,
  cellPerColumn,
  keyboardProps,
  measureRef,
  dataIndex,
  isActiveSticky,
  evictionAnchor,
  tableRef,
  className,
  style,
  ariaLevel,
  ariaExpanded,
}: SpacesListStickyGroupRowProps) => {
  const rowRef = useSpacesListStickyRowEviction(isActiveSticky, evictionAnchor, tableRef);

  const setRef = useCallback(
    (element: HTMLDivElement | null) => {
      measureRef(element);
      rowRef.current = element;
    },
    [measureRef, rowRef]
  );

  // Each deeper header pins one row-height further down, so the ancestors stack. `--sticky-level`
  // lets shallower headers paint on top of deeper ones. Both are read by the cells, not the row.
  // A static step assumes uniform row height (the list's rows are fixed-height); switch to summed
  // measured heights here if rows ever become variable.
  const top = `calc(var(--table-head-height) + ${level} * ${ROW_HEIGHT_ESTIMATE}px)`;
  const stickyStyle = isActiveSticky
    ? ({ ...style, "--sticky-top": top, "--sticky-level": level } as CSSProperties)
    : style;

  return (
    <TableSimpleRow
      itemId={node.id}
      item={node}
      columns={columns}
      isFirstColumnPinned={isFirstColumnPinned}
      cellPerColumn={cellPerColumn}
      keyboardProps={keyboardProps}
      measureRef={setRef}
      dataIndex={dataIndex}
      className={cx(className, { [styles.stickyRow]: isActiveSticky })}
      style={stickyStyle}
      ariaLevel={ariaLevel}
      ariaExpanded={ariaExpanded}
    />
  );
};

export default SpacesListStickyGroupRow;
